'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { Volume2 } from 'lucide-react'
import { KanaCell, KanaScript, hiraganaTable, katakanaTable } from './kana-data'
import KanaTableSection from '../_components/KanaTableSection'
import { getKanaAudioPath, type SelectedKana } from '../_lib/kana-practice'
import { Button } from '~/components/ui/button'

function getRomajiImageName(romaji: string) {
    const altMatch = romaji.match(/\(([^)]+)\)/)
    if (altMatch?.[1]) {
        return altMatch[1].trim().toLowerCase()
    }

    return romaji
        .replace(/\s*\(.+?\)/g, '')
        .trim()
        .toLowerCase()
}

function getStrokeImagePath(selected: SelectedKana) {
    const scriptFolder = selected.script === 'Hiragana' ? 'hira' : 'kana'
    const imageName = getRomajiImageName(selected.cell.romaji)
    return `/images/alphabet/${scriptFolder}/${imageName}.png`
}

export default function KanaAlphabetClient() {
    const [selected, setSelected] = useState<SelectedKana | null>(null)
    const [isAudioPlaying, setIsAudioPlaying] = useState(false)
    const [audioError, setAudioError] = useState('')
    const audioRef = useRef<HTMLAudioElement | null>(null)

    const audioPath = useMemo(() => {
        if (!selected) {
            return null
        }
        return getKanaAudioPath(selected)
    }, [selected])
    const hasStrokeAsset = selected?.cell.hasStrokeAsset !== false

    const playAudio = (path: string | null) => {
        if (!path) {
            return
        }

        const audio = audioRef.current ?? new Audio()
        audio.pause()
        audio.src = path
        audio.currentTime = 0
        audio.onended = () => setIsAudioPlaying(false)
        audio.onerror = () => {
            setIsAudioPlaying(false)
        }
        audioRef.current = audio

        setAudioError('')
        setIsAudioPlaying(true)
        audio.play().catch(() => {
            setIsAudioPlaying(false)
            setAudioError('Không thể phát audio. Trình duyệt đã chặn hoặc file bị lỗi.')
        })
    }

    const handleSelectKana = (cell: KanaCell, script: KanaScript) => {
        const nextSelected = { cell, script }
        setSelected(nextSelected)
        setAudioError('')
        playAudio(getKanaAudioPath(nextSelected))
    }

    const handlePlayAudio = () => {
        if (!selected) {
            return
        }

        playAudio(audioPath)
    }

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.onended = null
                audioRef.current.onerror = null
                audioRef.current = null
            }
        }
    }, [])

    useEffect(() => {
        setAudioError('')
    }, [selected?.cell.kana, selected?.script])

    return (
        <main className='mx-auto w-full max-w-(--breakpoint-xl) px-4 py-6 sm:px-6 sm:py-8'>
            <div className='mb-6 text-center sm:mb-8'>
                <h1 className='text-2xl font-bold text-foreground sm:text-3xl'>
                    Bảng Chữ Cái Tiếng Nhật
                </h1>
                <p className='mt-2 text-sm text-muted-foreground sm:text-base'>
                    Xem đầy đủ Hiragana và Katakana. Bấm vào ô ký tự để highlight và nghe phát âm ngay.
                </p>
            </div>

            {selected && (
                <section className='mb-6 rounded-xl border border-border bg-card p-4 text-center shadow-sm sm:p-6'>
                    <div className='mx-auto mt-4 max-w-xs rounded-lg border border-border bg-muted/40 p-3'>
                        <p className='mb-2 text-sm font-semibold text-foreground'>{selected.script}</p>
                        <p className='text-5xl leading-none font-bold text-foreground'>{selected.cell.kana}</p>
                        <p className='mt-1 text-base text-foreground'>{selected.cell.romaji}</p>

                        <Button
                            type='button'
                            onClick={handlePlayAudio}
                            disabled={!audioPath}
                            className='my-3 w-full'
                        >
                            <Volume2 className='size-4' aria-hidden='true' />
                            {isAudioPlaying ? 'Đang phát, phát lại' : 'Nghe phát âm'}
                        </Button>
                        {audioError && <p className='mt-2 text-xs text-destructive'>{audioError}</p>}

                        {hasStrokeAsset ? (
                            <Image
                                src={getStrokeImagePath(selected)}
                                alt={`Thứ tự nét của ${selected.cell.kana}`}
                                width={400}
                                height={400}
                                loading='lazy'
                                className='h-56 w-full rounded-md border border-border bg-white object-contain'
                            />
                        ) : (
                            <p className='rounded-md border border-dashed border-border px-3 py-6 text-sm text-muted-foreground'>
                                Chưa có ảnh thứ tự nét viết cho âm phiên âm mở rộng này.
                            </p>
                        )}
                    </div>
                </section>
            )}

            <div className='space-y-6'>
                <KanaTableSection table={hiraganaTable} selected={selected} onSelect={handleSelectKana} cardClassName='relative py-4' />
                <KanaTableSection table={katakanaTable} selected={selected} onSelect={handleSelectKana} cardClassName='relative py-4' />
            </div>
        </main>
    )
}
